import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import cookie from "react-cookies";
import {
  completeApplicationVideo,
  getMyApplication,
  putFileWithProgress,
  requestApplicationVideoUrl,
  saveMyApplication,
  submitMyApplication,
  withdrawMyApplication,
} from "../../services/jobsApi";
import { TrainingLoading } from "../academy/AcademyShared";
import { APPLICATION_STATUS, EMPLOYMENT_LABELS, JobPill } from "../jobs/jobsShared";
import MeLayout from "./MeLayout";

const MAX_BYTES = 200 * 1024 * 1024;

export default function MeApplication() {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({ cover_note: "", availability: "", expected_pay: "" });
  const [local, setLocal] = useState(null);
  const [busy, setBusy] = useState(false);
  const cancelRef = useRef(null);
  const previewRef = useRef(null);

  const applyApp = useCallback((next) => {
    setApp(next);
    setForm({
      cover_note: next.cover_note || "",
      availability: next.availability || "",
      expected_pay: next.expected_pay || "",
    });
  }, []);

  useEffect(() => {
    if (!cookie.load("candidateKey")) {
      window.location.href = `/login?next=/me/applications/${id}`;
      return;
    }
    getMyApplication(id)
      .then((res) => applyApp(res.application))
      .catch((e) => setError(e.message || "Could not load this application"))
      .finally(() => setLoading(false));
  }, [id, applyApp]);

  useEffect(
    () => () => {
      cancelRef.current?.();
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    },
    []
  );

  const setField = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const saveFields = async () => {
    const res = await saveMyApplication(id, form);
    setApp((prev) => ({ ...prev, ...res.application, play_url: prev?.play_url }));
    return res.application;
  };

  const onPickFile = async (file) => {
    if (!file) return;
    setError("");
    setMessage("");
    if (file.size > MAX_BYTES) {
      setError("Keep the video under 200 MB. About 30 to 60 seconds is plenty.");
      return;
    }
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    const previewUrl = URL.createObjectURL(file);
    previewRef.current = previewUrl;
    setLocal({ phase: "uploading", progress: 0, previewUrl });
    try {
      const prepared = await requestApplicationVideoUrl(id, {
        name: file.name,
        type: file.type,
        size: file.size,
      });
      const { promise, cancel } = putFileWithProgress(prepared.upload_url, file, {
        contentType: prepared.content_type || file.type,
        onProgress: (progress) => setLocal({ phase: "uploading", progress, previewUrl }),
      });
      cancelRef.current = cancel;
      await promise;
      cancelRef.current = null;
      const saved = await completeApplicationVideo(id, {
        s3_key: prepared.s3_key,
        content_type: prepared.content_type || file.type,
        byte_size: file.size,
        file_name: file.name,
      });
      setApp((prev) => ({ ...prev, ...saved.application, play_url: prev?.play_url }));
      setLocal({ phase: "uploaded", previewUrl });
      setMessage("Video uploaded. Send your application when you are happy with it.");
    } catch (e) {
      setLocal({ phase: "error", previewUrl });
      setError(e.message || "Upload failed");
    }
  };

  const onSubmit = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await saveFields();
      const res = await submitMyApplication(id);
      applyApp({ ...res.application, play_url: app?.play_url });
      setMessage(res.message || "Application sent.");
    } catch (e) {
      if (e.code === "profile_incomplete") {
        setError(e.message);
        setTimeout(() => {
          window.location.href = `${e.profile_path || "/me/profile"}?next=${encodeURIComponent(
            `/me/applications/${id}`
          )}`;
        }, 1800);
      } else {
        setError(e.message || "Could not send your application");
      }
    } finally {
      setBusy(false);
    }
  };

  const onSaveDraft = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await saveFields();
      setMessage("Saved.");
    } catch (e) {
      setError(e.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const onWithdraw = async () => {
    if (!window.confirm("Withdraw this application?")) return;
    setBusy(true);
    setError("");
    try {
      const res = await withdrawMyApplication(id);
      applyApp({ ...res.application, play_url: app?.play_url });
    } catch (e) {
      setError(e.message || "Could not withdraw");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <MeLayout title="Application" backTo="/academy">
        <TrainingLoading label="Loading application..." />
      </MeLayout>
    );
  }

  if (!app) {
    return (
      <MeLayout title="Application" backTo="/academy">
        <div className="job-app">
          <p className="job-app__error">{error || "Application not found"}</p>
          <Link to="/me/applications" className="training-btn-secondary">
            My applications
          </Link>
        </div>
      </MeLayout>
    );
  }

  const position = app.position || {};
  const meta = APPLICATION_STATUS[app.status] || { label: app.status, tone: "slate" };
  const editable = app.can_edit;
  const previewSrc = local?.previewUrl || app.play_url || null;
  const uploading = local?.phase === "uploading";

  return (
    <MeLayout title={position.title || "Application"} backTo="/academy">
      <div className="job-app">
        <div className="job-app__row">
          <JobPill tone={meta.tone}>{meta.label}</JobPill>
          <span>
            {EMPLOYMENT_LABELS[position.employment_type] || ""}
            {position.pay_summary ? ` · ${position.pay_summary}` : ""}
          </span>
        </div>

        {position.slug ? (
          <p style={{ marginTop: 8 }}>
            <Link to={`/jobs/${position.slug}`}>View the role</Link>
          </p>
        ) : null}

        {!editable ? (
          <p className="training-lead" style={{ marginTop: 16 }}>
            {app.status === "submitted"
              ? "Your application is in. We will contact you if we want to take it further."
              : app.status === "shortlisted"
                ? "You are on our shortlist. We will contact you about next steps."
                : app.status === "interview"
                  ? "We want to talk to you. Keep your phone close."
                  : app.status === "hired"
                    ? "You got the role. We will be in touch with the details."
                    : app.status === "rejected"
                      ? "Thanks for applying. We went with other candidates this time. Keep an eye on new roles."
                      : "This application is closed."}
          </p>
        ) : null}

        {position.video_prompt && editable ? (
          <div className="job-app__prompt">
            <strong>What to say in your video</strong>
            <p>{position.video_prompt}</p>
          </div>
        ) : null}

        {editable ? (
          <>
            <div className="job-app__field">
              <label htmlFor="cover_note">Why you, in a few lines</label>
              <textarea
                id="cover_note"
                maxLength={2000}
                value={form.cover_note}
                onChange={setField("cover_note")}
              />
            </div>
            <div className="job-app__field">
              <label htmlFor="availability">When can you start</label>
              <input
                id="availability"
                maxLength={120}
                value={form.availability}
                onChange={setField("availability")}
              />
            </div>
            <div className="job-app__field">
              <label htmlFor="expected_pay">What do you expect to earn (optional)</label>
              <input
                id="expected_pay"
                maxLength={120}
                value={form.expected_pay}
                onChange={setField("expected_pay")}
              />
            </div>
          </>
        ) : (
          <>
            {app.cover_note ? <p style={{ marginTop: 16 }}>{app.cover_note}</p> : null}
          </>
        )}

        <div className="job-app__field">
          <label>
            Intro video{position.video_required ? "" : " (optional)"}
          </label>
          {previewSrc ? (
            <video
              key={previewSrc}
              className="job-app__video"
              src={previewSrc}
              controls
              playsInline
            />
          ) : null}
          {uploading ? <p>Uploading... {local.progress || 0}%</p> : null}
          {editable ? (
            <label className="training-btn-secondary" style={{ cursor: "pointer", width: "fit-content" }}>
              {app.has_file || local?.previewUrl ? "Replace video" : "Choose video"}
              <input
                type="file"
                hidden
                accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
                disabled={uploading}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  onPickFile(file);
                }}
              />
            </label>
          ) : null}
        </div>

        {editable ? (
          <p className="job-app__consent">
            By sending this application you agree that QikTruck can store and watch your video to
            assess you for this role. We do not publish it.
          </p>
        ) : null}

        {error ? <p className="job-app__error">{error}</p> : null}
        {message ? <p className="job-app__ok">{message}</p> : null}

        <div className="job-app__actions">
          {editable ? (
            <>
              <button
                type="button"
                className="training-btn-primary"
                disabled={busy || uploading || (position.video_required && !app.has_file)}
                onClick={onSubmit}
              >
                {busy ? "Working..." : "Send application"}
              </button>
              <button
                type="button"
                className="training-btn-secondary"
                disabled={busy || uploading}
                onClick={onSaveDraft}
              >
                Save draft
              </button>
            </>
          ) : null}
          {app.can_withdraw ? (
            <button
              type="button"
              className="training-btn-secondary"
              disabled={busy}
              onClick={onWithdraw}
            >
              Withdraw
            </button>
          ) : null}
          <Link to="/me/applications" className="training-btn-secondary">
            All applications
          </Link>
        </div>
      </div>
    </MeLayout>
  );
}
